/**
 * Typed HTTP client for the LFS ERP FastAPI backend.
 * Same `/api/*` contract as the mobile app.
 */

const RAW_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API_BASE = RAW_BASE ? `${RAW_BASE}/api` : "/api";

const TOKEN_KEY = "lfs_erp_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export interface HttpOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  skipAuth?: boolean;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
  }
}

export async function http<T = any>(path: string, opts: HttpOptions = {}): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers || {}),
  };
  if (!opts.skipAuth) {
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }
  const res = await fetch(url.toString(), {
    method: opts.method || "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let detail: any = null;
    try { detail = await res.json(); } catch { /* ignore */ }
    const msg = detail?.detail || detail?.message || res.statusText || "Request failed";
    throw new ApiError(res.status, typeof msg === "string" ? msg : JSON.stringify(msg), detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ----- Typed endpoints -----

export interface UserPublic {
  id: string;
  email: string;
  name: string;
  role: "admin" | "principal" | "teacher" | "student" | "parent" | "cashier" | "examhead";
  class_name?: string | null;
  subject?: string | null;
  child_email?: string | null;
  phone?: string | null;
  must_reset_password?: boolean;
  permissions?: string[];
}

export interface LoginResp { access_token: string; token_type: string }

export const api = {
  login: (email: string, password: string) =>
    http<LoginResp>("/auth/login", { method: "POST", body: { email, password }, skipAuth: true }),
  me: () => http<UserPublic>("/auth/me"),
  stats: () => http<Record<string, any>>("/stats"),
  academicYears: {
    list: () => http<AcademicYear[]>("/academic-years/"),
    current: () => http<AcademicYear>("/academic-years/current"),
    setCurrent: (id: string) =>
      http<{ ok: true; current_year_id: string }>(
        `/academic-years/${id}/set-current`, { method: "POST" },
      ),
    create: (data: Partial<AcademicYear>) =>
      http<AcademicYear>("/academic-years/", { method: "POST", body: data }),
  },
};

export interface AcademicYear {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
}
